import type { DriverShipment } from "@khazzin/api-client";
import { StatusChip } from "@khazzin/ui/patterns/StatusChip";
import { Field, Input, NumberInput } from "@khazzin/ui/primitives";
import { Button } from "@khazzin/ui/primitives/Button";
import { ConfirmDialog } from "@khazzin/ui/primitives/Dialog";
import { useState } from "react";
import { useI18n } from "../../i18n";
import { useCommand } from "../../offline/Offline";
import { withStop } from "./stops";

/**
 * A pickup stop's block (EP03-S21, DA-10-22): what to collect, and the two
 * outcomes — Collected, with the count actually handed over, or Not
 * collected, with why. Each is a queued command (`collect_pickup`,
 * `fail_pickup`) like every other, so it works with no signal and shows at
 * once; the server snapshots the store's fee when the collection lands.
 */
/**
 * The usual reasons a pickup fails (ADJ-M03-4). The server takes free text,
 * so a preset is sent as a fixed English phrase — the same words on the desk
 * whatever the phone's language — and Other as what the driver typed.
 */
const PICKUP_REASON_TEXT = {
	store_closed: "Store closed",
	not_ready: "Parcels not ready",
	unreachable: "Contact unreachable",
	address_wrong: "Wrong address",
	other: "",
} as const;

type PickupReason = keyof typeof PICKUP_REASON_TEXT;

const PICKUP_REASONS = Object.keys(PICKUP_REASON_TEXT) as PickupReason[];

/** The reason sent, or "" while there is none to send. */
export function pickupReason(preset: PickupReason | null, typed: string): string {
	if (preset === null) {
		return "";
	}
	return preset === "other" ? typed.trim() : PICKUP_REASON_TEXT[preset];
}

export function PickupOutcome({ stop }: { stop: DriverShipment }) {
	const { t } = useI18n();
	const collect = useCommand("collect_pickup");
	const fail = useCommand("fail_pickup");
	const [outcome, setOutcome] = useState<"collected" | "failed" | null>(null);
	const [count, setCount] = useState(String(stop.expectedCount ?? 1));
	const [preset, setPreset] = useState<PickupReason | null>(null);
	const [reason, setReason] = useState("");
	const failReason = pickupReason(preset, reason);
	const open = stop.status === "driver_assigned";
	const counted = Number(count);
	const countOk = /^[1-9][0-9]*$/.test(count.trim());

	async function confirm() {
		if (outcome === "collected") {
			await collect(
				{ collectedCount: counted },
				{
					shipmentId: stop.shipmentId,
					apply: (d) => withStop(d, stop.shipmentId, { status: "collected" }),
				},
			);
		} else {
			await fail(
				{ reason: failReason },
				{
					shipmentId: stop.shipmentId,
					apply: (d) => withStop(d, stop.shipmentId, { status: "failed" }),
				},
			);
		}
	}

	return (
		<section aria-label={t("driver.pickup.title")} className="flex flex-col gap-3 card p-4">
			<h2 className="text-[15px] font-bold text-fg">{t("driver.pickup.title")}</h2>
			<div className="flex flex-wrap items-center gap-2">
				<span className="me-auto text-[14px] text-fg">{stop.trackingNumber}</span>
				<StatusChip tone="brand" size="sm">
					{t(`driver.pickup.type.${stop.pickupType ?? "inbound_stock"}`)}
				</StatusChip>
				<StatusChip tone="neutral" size="sm">
					{t("driver.today.parcels", { count: stop.expectedCount ?? 1 })}
				</StatusChip>
			</div>
			{open && (
				<div className="grid grid-cols-2 gap-2">
					<Button variant="primary" size="lg" onClick={() => setOutcome("collected")}>
						{t("driver.pickup.collected")}
					</Button>
					<Button size="lg" onClick={() => setOutcome("failed")}>
						{t("driver.pickup.failed")}
					</Button>
				</div>
			)}
			<ConfirmDialog
				open={outcome !== null}
				onOpenChange={(next) => !next && setOutcome(null)}
				title={t(outcome === "failed" ? "driver.pickup.failTitle" : "driver.pickup.collectTitle")}
				confirmLabel={t(outcome === "failed" ? "driver.pickup.failed" : "driver.pickup.collected")}
				cancelLabel={t("driver.stop.pinCancel")}
				closeLabel={t("driver.stop.pinCancel")}
				{...(outcome === "failed" ? { tone: "danger" as const } : {})}
				confirmDisabled={outcome === "failed" ? failReason === "" : !countOk}
				onConfirm={confirm}
			>
				{outcome === "collected" ? (
					<Field label={t("driver.pickup.count")} hint={t("driver.pickup.countHint")} required>
						{(control) => (
							<NumberInput
								{...control}
								integerOnly
								value={count}
								onChange={(e) => setCount(e.target.value)}
							/>
						)}
					</Field>
				) : (
					<div className="flex flex-col gap-3">
						{/* ADJ-M03-4: the usual reasons are one tap; Other takes words. */}
						<fieldset className="flex flex-col gap-1">
							<legend className="mb-2 text-[14px] font-bold text-fg">
								{t("driver.pickup.reason")}
							</legend>
							{PICKUP_REASONS.map((key) => (
								<label
									key={key}
									className="flex min-h-12 items-center gap-3 rounded-lg border border-border px-3 text-[15px] text-fg has-checked:border-brand has-checked:bg-brand-subtle"
								>
									<input
										type="radio"
										name="pickup-reason"
										className="size-5 accent-brand"
										checked={preset === key}
										onChange={() => setPreset(key)}
									/>
									{t(`driver.pickup.reasons.${key}`)}
								</label>
							))}
						</fieldset>
						{preset === "other" && (
							<Field label={t("driver.pickup.otherReason")} required>
								{(control) => (
									<Input {...control} value={reason} onChange={(e) => setReason(e.target.value)} />
								)}
							</Field>
						)}
					</div>
				)}
			</ConfirmDialog>
		</section>
	);
}
